import React from 'react';
import { Badge } from '../Badge/Badge';
import { Image } from '../Image/Image';
import { Video } from '../Video/Video';
import './FeaturedColumn.scss';

/**
 * FeaturedColumn - Promotional card column within a dropdown panel
 *
 * Renders a clickable featured content card alongside LinkColumns in a DropdownPanel.
 *
 * Features:
 * - Image using existing Image component (or Video lightbox when videoUrl is set)
 * - Badge with optional icon
 * - Heading and subheading text
 * - Self-contained: receives all data as props, no knowledge of siblings
 *
 * Click behavior:
 * - When `videoUrl` is set: media area becomes a Video lightbox trigger (button)
 *   that opens the embedded player modal. The text body is plain (no link).
 *   This avoids invalid `<a><button>` nesting.
 * - When `videoUrl` is not set: the entire card is a single link to `url` with
 *   analytics tracking.
 */

export interface FeaturedColumnProps {
  /** Featured image URL (used as Video poster when videoUrl is set) */
  imageUrl: string;
  /** Link URL for the entire featured column (ignored when videoUrl is set) */
  url?: string;
  /** CTA ID for analytics tracking */
  ctaId?: string;
  /** Main heading text */
  title: string;
  /** Subheading text below the title */
  description?: string;
  /** Badge text (e.g. "New") */
  badgeText?: string;
  /** Badge icon URL */
  badgeIconUrl?: string;
  /**
   * Video URL (YouTube, MP4, or page URL). When set, the media area renders a
   * Video lightbox trigger instead of a plain image, and clicking the play
   * icon opens an embedded player modal.
   */
  videoUrl?: string;
  /** Size variant: 'sm' for 3+ column layouts, 'lg' for 2 column layouts */
  size?: 'sm' | 'lg';
}

export const FeaturedColumn: React.FC<FeaturedColumnProps> = ({
  imageUrl,
  url,
  ctaId,
  title,
  description,
  badgeText,
  badgeIconUrl,
  videoUrl,
  size = 'sm',
}) => {
  const body = (
    <div className="poly-featured-column__body">
      {badgeText && (
        <Badge
          size="sm"
          tone="dark"
          iconUrl={badgeIconUrl}
          className="poly-featured-column__badge"
        >
          {badgeText}
        </Badge>
      )}
      <span className="poly-featured-column__title">{title}</span>
      {description && (
        <span className="poly-featured-column__description">{description}</span>
      )}
    </div>
  );

  // Video variant: Video is a <button> (lightbox trigger), so the card cannot
  // be wrapped in a single <a>. Use <article> + sibling body (matches CardGrid
  // pattern).
  if (videoUrl) {
    return (
      <article className={`poly-featured-column poly-featured-column--${size} poly-featured-column--video`}>
        <Video
          posterUrl={imageUrl}
          posterAlt={title}
          videoUrl={videoUrl}
          playBehavior="lightbox"
          className="poly-featured-column__media"
        />
        {body}
      </article>
    );
  }

  // Default: entire card is a single link.
  return (
    <article className={`poly-featured-column poly-featured-column--${size}`}>
      <a
        href={url}
        className="poly-featured-column__link"
        data-cta-id={ctaId || undefined}
      >
        <div className="poly-featured-column__image-wrapper">
          <Image
            url={imageUrl}
            alt={title}
            objectFit="cover"
            className="poly-featured-column__image"
          />
        </div>
        {body}
      </a>
    </article>
  );
};
